import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BlogEditor from './components/BlogEditor';
import Navbar from '@/pages/home/components/Navbar';
import type { BlogPost } from '@/mocks/blogData';
import { blogPosts as initialPosts } from '@/mocks/blogData';
import useSeo, { SITE_URL } from '@/hooks/useSeo';

interface EditablePost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  coverImage: string;
  category: string;
  tags: string[];
  publishedAt: string;
  readTime: string;
  featured: boolean;
  author: string;
}

function createEmptyPost(): EditablePost {
  const id = Date.now().toString();
  return {
    id,
    slug: '',
    title: '',
    excerpt: '',
    content: '',
    coverImage: '',
    category: 'Mindset',
    tags: [],
    publishedAt: new Date().toISOString().split('T')[0],
    readTime: '1 min read',
    featured: false,
    author: 'TNT Coach AI',
  };
}

const categories = ['Mindset', 'Productivity', 'Leadership', 'Wellness'];

export default function BlogAdminPage() {
  const navigate = useNavigate();

  useSeo({
    title: 'Blog Admin | Top Notch Technologies',
    description: 'Internal blog management area.',
    canonical: `${SITE_URL}/blog/admin`,
    url: `${SITE_URL}/blog/admin`,
    noindex: true,
  });

  const [posts, setPosts] = useState<EditablePost[]>(() =>
    initialPosts.map((p) => ({ ...p }))
  );
  const [editingPost, setEditingPost] = useState<EditablePost | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [coverPreview, setCoverPreview] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [docImporting, setDocImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  const currentPost = editingPost;

  const handleCreate = () => {
    setEditingPost(createEmptyPost());
    setIsCreating(true);
    setCoverPreview('');
    setTagInput('');
  };

  const handleEdit = (post: EditablePost) => {
    setEditingPost({ ...post });
    setIsCreating(false);
    setCoverPreview(post.coverImage);
    setTagInput('');
  };

  const handleCancel = () => {
    setEditingPost(null);
    setIsCreating(false);
    setCoverPreview('');
  };

  const handleSave = () => {
    if (!currentPost) return;
    if (!currentPost.title.trim()) {
      alert('Please enter a post title.');
      return;
    }
    const slug = currentPost.slug || currentPost.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const wordCount = currentPost.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
    const readMinutes = Math.max(1, Math.ceil(wordCount / 200));

    const saved: EditablePost = {
      ...currentPost,
      slug,
      readTime: `${readMinutes} min read`,
      content: currentPost.content,
    };

    if (isCreating) {
      setPosts([saved, ...posts]);
    } else {
      setPosts(posts.map((p) => (p.id === saved.id ? saved : p)));
    }

    setEditingPost(null);
    setIsCreating(false);
    setCoverPreview('');
  };

  const handleDelete = (postId: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    setPosts(posts.filter((p) => p.id !== postId));
    if (editingPost?.id === postId) {
      setEditingPost(null);
      setIsCreating(false);
    }
  };

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      setCoverPreview(url);
      if (currentPost) {
        setEditingPost({ ...currentPost, coverImage: url });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentPost) return;

    setDocImporting(true);
    try {
      if (file.name.endsWith('.docx')) {
        const mammoth = await import('mammoth');
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setEditingPost({
          ...currentPost,
          content: result.value,
        });
      } else if (file.name.endsWith('.txt')) {
        const text = await file.text();
        const html = text
          .split('\n\n')
          .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
          .join('');
        setEditingPost({
          ...currentPost,
          content: html,
        });
      } else {
        alert('Please upload a .docx or .txt file.');
      }
    } catch (err) {
      alert('Failed to read the document. Please try again.');
      console.error(err);
    } finally {
      setDocImporting(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handlePreview = () => {
    if (!currentPost) return;
    const slug = currentPost.slug || currentPost.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    window.open(`/blog/${slug}`, '_blank');
  };

  const addTag = () => {
    if (!currentPost || !tagInput.trim()) return;
    const tag = tagInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (currentPost.tags.includes(tag)) {
      setTagInput('');
      return;
    }
    setEditingPost({ ...currentPost, tags: [...currentPost.tags, tag] });
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    if (!currentPost) return;
    setEditingPost({ ...currentPost, tags: currentPost.tags.filter((t) => t !== tag) });
  };

  const updateField = (field: keyof EditablePost, value: any) => {
    if (!currentPost) return;
    setEditingPost({ ...currentPost, [field]: value });
  };

  const updateContent = useCallback((html: string) => {
    if (!currentPost) return;
    setEditingPost((prev) => prev ? { ...prev, content: html } : null);
  }, [currentPost]);

  const navTabs = [
    { label: 'Website', path: '/' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <div className="min-h-screen bg-midnight">
      <Navbar />

      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-warm-white">Blog Admin</h1>
              <p className="text-silver-grey text-sm mt-1">Manage your blog posts</p>
            </div>
            <div className="flex items-center gap-3">
              {navTabs.map((tab) => (
                <button
                  key={tab.path}
                  onClick={() => navigate(tab.path)}
                  className="px-3 py-1.5 rounded-full bg-royal-navy/30 text-silver-grey text-xs font-medium hover:text-warm-white hover:bg-royal-navy/50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {tab.label}
                  <i className="ri-external-link-line ml-1 text-[10px]" />
                </button>
              ))}
              {!editingPost && (
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full gold-gradient text-midnight font-semibold text-sm hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line" />
                  New Post
                </button>
              )}
            </div>
          </div>

          {!editingPost ? (
            <div className="bg-royal-navy/5 border border-royal-navy/20 rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-royal-navy/20 bg-royal-navy/10 text-xs font-semibold text-silver-grey uppercase tracking-wider">
                <div className="col-span-5">Title</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="grid grid-cols-12 gap-4 px-5 py-3.5 border-b border-royal-navy/10 hover:bg-royal-navy/10 transition-colors items-center"
                >
                  <div className="col-span-5">
                    <p className="text-warm-white text-sm font-medium truncate">{post.title}</p>
                    <p className="text-taupe-grey text-xs truncate mt-0.5">{post.slug}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="px-2 py-0.5 rounded-full bg-royal-navy/30 text-silver-grey text-xs">
                      {post.category}
                    </span>
                  </div>
                  <div className="col-span-2 text-silver-grey text-xs">{post.publishedAt}</div>
                  <div className="col-span-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      post.featured
                        ? 'bg-champagne-gold/20 text-champagne-gold border border-champagne-gold/30'
                        : 'bg-royal-navy/20 text-silver-grey'
                    }`}>
                      {post.featured ? 'Featured' : 'Draft'}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleEdit(post)}
                      className="w-8 h-8 flex items-center justify-center rounded bg-royal-navy/30 text-silver-grey hover:text-warm-white hover:bg-royal-navy/50 transition-colors cursor-pointer text-sm"
                      title="Edit"
                    >
                      <i className="ri-edit-line" />
                    </button>
                    <button
                      onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                      className="w-8 h-8 flex items-center justify-center rounded bg-royal-navy/30 text-silver-grey hover:text-warm-white hover:bg-royal-navy/50 transition-colors cursor-pointer text-sm"
                      title="Preview"
                    >
                      <i className="ri-eye-line" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="w-8 h-8 flex items-center justify-center rounded bg-royal-navy/30 text-silver-grey hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer text-sm"
                      title="Delete"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>
              ))}
              {posts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-silver-grey text-sm">No posts yet. Create your first one!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCancel}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-royal-navy/30 text-silver-grey hover:text-warm-white hover:bg-royal-navy/50 transition-colors cursor-pointer"
                    title="Back to posts"
                  >
                    <i className="ri-arrow-left-line" />
                  </button>
                  <h2 className="text-xl font-heading font-semibold text-warm-white">
                    {isCreating ? 'Create New Post' : 'Edit Post'}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreview}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-royal-navy/30 text-silver-grey text-sm font-medium hover:text-warm-white hover:bg-royal-navy/50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-eye-line" />
                    Preview
                  </button>
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full gold-gradient text-midnight font-semibold text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-check-line" />
                    {isCreating ? 'Publish' : 'Save Changes'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <label className="block text-silver-grey text-sm font-medium mb-1.5">Title</label>
                    <input
                      type="text"
                      value={currentPost.title}
                      onChange={(e) => updateField('title', e.target.value)}
                      placeholder="Enter post title..."
                      className="w-full px-4 py-2.5 rounded-lg bg-royal-navy/10 border border-royal-navy/40 text-warm-white text-sm placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/50 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-silver-grey text-sm font-medium mb-1.5">Excerpt</label>
                    <textarea
                      value={currentPost.excerpt}
                      onChange={(e) => updateField('excerpt', e.target.value)}
                      rows={2}
                      placeholder="Short description of the post..."
                      className="w-full px-4 py-2.5 rounded-lg bg-royal-navy/10 border border-royal-navy/40 text-warm-white text-sm placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/50 transition-colors resize-none"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-silver-grey text-sm font-medium">Content</label>
                      <button
                        onClick={() => docInputRef.current?.click()}
                        disabled={docImporting}
                        className="inline-flex items-center gap-1 text-xs text-champagne-gold hover:text-champagne-gold/80 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        {docImporting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <i className="ri-file-word-2-line" />
                            Import Word Doc
                          </>
                        )}
                      </button>
                      <input
                        ref={docInputRef}
                        type="file"
                        accept=".docx,.txt"
                        onChange={handleDocUpload}
                        className="hidden"
                      />
                    </div>
                    <BlogEditor initialHtml={currentPost.content} onChange={updateContent} />
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-5 rounded-lg bg-royal-navy/5 border border-royal-navy/20 space-y-4">
                    <h3 className="text-sm font-semibold text-warm-white uppercase tracking-wider">Post Settings</h3>

                    <div>
                      <label className="block text-silver-grey text-xs font-medium mb-1">Category</label>
                      <select
                        value={currentPost.category}
                        onChange={(e) => updateField('category', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-royal-navy/10 border border-royal-navy/40 text-warm-white text-sm focus:outline-none focus:border-champagne-gold/50 transition-colors cursor-pointer"
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-silver-grey text-xs font-medium cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentPost.featured}
                          onChange={(e) => updateField('featured', e.target.checked)}
                          className="rounded accent-champagne-gold cursor-pointer"
                        />
                        Featured Post
                      </label>
                    </div>

                    <div>
                      <label className="block text-silver-grey text-xs font-medium mb-1">Tags</label>
                      <div className="flex gap-1.5 mb-2">
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                          placeholder="Add a tag..."
                          className="flex-1 px-3 py-1.5 rounded-lg bg-royal-navy/10 border border-royal-navy/40 text-warm-white text-xs placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/50 transition-colors"
                        />
                        <button
                          onClick={addTag}
                          className="px-2 py-1.5 rounded-lg bg-royal-navy/30 text-silver-grey text-xs hover:text-warm-white transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {currentPost.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-royal-navy/20 text-silver-grey text-xs border border-royal-navy/30"
                          >
                            #{tag}
                            <button
                              onClick={() => removeTag(tag)}
                              className="text-taupe-grey hover:text-red-400 transition-colors cursor-pointer"
                            >
                              <i className="ri-close-line text-[10px]" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="p-5 rounded-lg bg-royal-navy/5 border border-royal-navy/20 space-y-4">
                    <h3 className="text-sm font-semibold text-warm-white uppercase tracking-wider">Cover Image</h3>
                    {coverPreview ? (
                      <div className="relative rounded-lg overflow-hidden aspect-video">
                        <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover" />
                        <button
                          onClick={() => {
                            setCoverPreview('');
                            if (currentPost) updateField('coverImage', '');
                          }}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-midnight/80 text-silver-grey hover:text-red-400 flex items-center justify-center transition-colors cursor-pointer text-xs"
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-royal-navy/40 rounded-lg aspect-video flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-champagne-gold/50 transition-colors"
                      >
                        <i className="ri-image-add-line text-2xl text-taupe-grey" />
                        <span className="text-taupe-grey text-xs">Click to upload cover image</span>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleCoverUpload}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}